/**
 * The one thing Horizon may interrupt you about, and nothing else.
 *
 * A repeating expense waiting for one tap. Dismissible, and it does not reappear
 * once dealt with.
 *
 * There used to be a second banner here — an offer to suggest budgets from
 * observed spending. It is gone, and `docs/SPEC.md` §5.2 records why: it wrote
 * budgets to **savings** categories, whose amounts come off the salary before
 * anything is spent, so a "suggestion" moved what the user was allowed to spend
 * by tens of thousands without showing a single figure first.
 */

import { useSuccessFeedback } from "@/hooks/use_success_feedback";
import { useMemo, useState } from "react";

import { formatMoney } from "@/domain/money";
import { pendingRecurring } from "@/domain/recurring";
import { Label, cx } from "@/features/common/primitives";
import { useCategories, useProfile, useRecurringRules } from "@/hooks/use_data";
import { addExpense, updateRecurringRule } from "@/lib/repo";

export function PendingRecurringBanner(): React.JSX.Element | null {
  const feedback = useSuccessFeedback();
  const rules = useRecurringRules();
  const categories = useCategories();
  const profile = useProfile();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pending = useMemo(
    () => pendingRecurring(rules, new Date(), profile.timezone),
    [rules, profile.timezone],
  );

  const next = pending[0];
  if (!next) return feedback.version > 0 ? <p key={feedback.version} role="status" className="feel-success px-phi-1 py-phi-n2 text-phi-n1 text-acc">Repeating expense saved</p> : null;

  const category = categories.find((c) => c.id === next.rule.categoryId);

  const confirm = (): void => {
    setBusy(next.rule.id);
    // Two writes, deliberately in this order: log the expense, then advance the
    // rule. If the second fails the pending row simply reappears — far better
    // than advancing first and silently losing the expense.
    void addExpense({
      categoryId: next.rule.categoryId,
      amountCents: next.rule.amountCents,
      occurredAt: next.occurredAt.toISOString(),
      note: next.rule.note,
      recurringRuleId: next.rule.id,
    })
      .then(() => updateRecurringRule(next.rule.id, { lastMaterialisedOn: next.dueOn }))
      .then(feedback.success)
      .catch((cause: unknown) => {
        feedback.clear();
        setError(
          cause instanceof Error
            ? `Couldn't log that: ${cause.message}`
            : "Couldn't log that repeating expense.",
        );
      })
      .finally(() => setBusy(null));
  };

  const skip = (): void => {
    setBusy(next.rule.id);
    void updateRecurringRule(next.rule.id, { lastMaterialisedOn: next.dueOn })
      .catch((cause: unknown) => {
        feedback.clear();
        setError(
          cause instanceof Error
            ? `Couldn't skip that: ${cause.message}`
            : "Couldn't skip that. It is still due.",
        );
      })
      .finally(() => setBusy(null));
  };

  return (
    <div className="border-t border-line bg-bg-2 px-phi-1 py-phi-n1">
      {feedback.version > 0 ? <p key={feedback.version} role="status" className="feel-success text-phi-n1 text-acc">Repeating expense saved</p> : null}
      <Label>Repeating expense due</Label>
      <p className="pt-phi-n6 text-phi-n1">
        <span className="font-semibold">{next.rule.note ?? category?.name ?? "Expense"}</span>{" "}
        <span className="tnum text-tx-2">
          {formatMoney(next.rule.amountCents, profile.currency)}
        </span>
      </p>
      <p className="pb-phi-n1 text-phi-n1 text-tx-2">
        Due {next.dueOn}. Nothing is logged until you confirm — so if the amount
        changed, edit it after.
      </p>
      {error ? (
        <p role="alert" className="pb-phi-n3 text-phi-n1 text-over">
          {error}
        </p>
      ) : null}
      <div className="flex gap-phi-n1">
        <button
          type="button"
          onClick={confirm}
          disabled={busy !== null}
          className={cx(
            "min-h-[44px] flex-1 rounded-phi-n2 bg-acc text-phi-n1 font-semibold text-on-acc",
            "disabled:opacity-40",
          )}
        >
          Log it
        </button>
        <button
          type="button"
          onClick={skip}
          disabled={busy !== null}
          className="min-h-[44px] rounded-phi-n2 border border-line px-phi-0 text-phi-n1 font-medium text-tx-2 disabled:opacity-40"
        >
          Not this month
        </button>
      </div>
    </div>
  );
}
